import { IconInfo } from "@aksara-ui/icons";
import {
  Avatar,
  Badge,
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableBodyCell,
  TableBodyRow,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  theme,
} from "@aksara-ui/react";
import { InputSearchbox } from "components/Form/InputSearchbox";
import {
  Pagination,
  PaginationDetail,
  PaginationFilter,
} from "components/Pagination";
import TableBodyRowsSkeleton from "components/TableBodyRowSkeleton";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMediaQuery } from "react-responsive";
import {
  getAllMembers,
  getAllTenantsFilter,
  setModalFormOpen,
  storeRaffleBlacklist,
} from "redux/actions";
import styled from "styled-components";
import { toCapitilizeCase } from "utilities/helper/toCapitalizeCase";
import { MemberDetailModal } from "./components";
import FilterKit from "components/FilterKit";
import { InputCheckbox } from "components/Form/InputCheckbox";
import { getRole } from "hooks";
import { stringToColor } from "utilities/helper/stringToColor";

const HeaderWrapper = styled(Box)`
  justify-content: space-between;
  margin-bottom: 30px;
  gap: 20px;
`;

const MembersContent: React.FC = () => {
  const dispatch = useDispatch();
  const userRole = getRole();
  const isMobile = useMediaQuery({ maxWidth: 576 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });

  const { isLoading, isModalFormOpen } = useSelector(
    (state: any) => state.globalReducer
  );
  const { members, memberTierFilters } = useSelector(
    (state: any) => state.memberReducer
  );
  const {
    currentPage,
    totalPage,
    limitPage,
    lengthPage,
    filterTenantPage,
    filterTierPage,
    filterPortalPage,
    filterBlacklistPage,
  } = useSelector((state: any) => state.memberReducer);
  const { tenantsFilter } = useSelector((state: any) => state.tenantReducer);

  const [searchValue, setSearchValue] = useState("");
  const [limit, setLimit] = useState(10);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [blacklistStates, setBlacklistStates] = useState<Record<string, boolean>>({});

  const handleSearchChange = (event: any) => {
    setSearchValue(event.target.value);
    dispatch(
      getAllMembers(
        1,
        event.target.value,
        limitPage,
        filterTenantPage,
        filterTierPage,
        filterPortalPage,
        filterBlacklistPage
      ) as any
    );
  };

  const handleClearSearchChange = () => {
    setSearchValue("");
    dispatch(
      getAllMembers(
        currentPage,
        "",
        limitPage,
        filterTenantPage,
        filterTierPage,
        filterPortalPage,
        filterBlacklistPage
      ) as any
    );
  };

  const handleOpenFormModal = (memberId: string = "") => {
    dispatch(setModalFormOpen(true) as any);
    setSelectedMemberId(memberId);
  };

  const handleCloseFormModal = () => {
    dispatch(setModalFormOpen(false) as any);
    setSelectedMemberId("");
  };

  useEffect(() => {
    if (!members.length) {
      dispatch(getAllTenantsFilter() as any);
      dispatch(
        getAllMembers(
          currentPage,
          "",
          limitPage,
          filterTenantPage,
          filterTierPage,
          filterPortalPage,
          filterBlacklistPage
        ) as any
      );
    }
  }, [dispatch]);

  useEffect(() => {
    const initialBlacklist = members.reduce(
      (acc: Record<string, boolean>, member: any) => {
        acc[member.id] = Boolean(member.is_blacklist_raffle);
        return acc;
      },
      {}
    );
    setBlacklistStates(initialBlacklist);
  }, [members]);

  const handleBlacklistToggle = async (memberId: string) => {
    const currentValue = !!blacklistStates[memberId];
    const nextValue = !currentValue;
    setBlacklistStates((prev) => ({ ...prev, [memberId]: nextValue }));

    try {
      await dispatch(storeRaffleBlacklist(memberId) as any);
    } catch (error) {
      setBlacklistStates((prev) => ({ ...prev, [memberId]: currentValue }));
    }
  };

  return (
    <Box height={"100%"} paddingY={20} paddingX={isMobile ? 30 : 50}>
      <HeaderWrapper
        display={"flex"}
        flexDirection={"column"}
        style={{ gap: 20 }}
      >
        <Box display={"flex"}>
          <Text scale={[500, 600]} fontWeight={700}>
            Membership List
          </Text>
        </Box>

        <Box
          display={"flex"}
          flexDirection={isTablet ? "column" : "row"}
          style={{ gap: 5 }}
        >
          <InputSearchbox
            autoComplete="off"
            name="textDummy"
            placeholder="Search..."
            size={32}
            onChange={handleSearchChange}
            onClearButton={handleClearSearchChange}
          />

          {userRole === "superadmin" ? (
            <FilterKit
              orderByOptions={[
                {
                  key: tenantsFilter,
                  label: "Tenant",
                  options: tenantsFilter.map((tenant: any) => {
                    return {
                      label: tenant.name,
                      value: tenant.id,
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllMembers(
                    1,
                    "",
                    limitPage,
                    e[tenantsFilter] ? e[tenantsFilter].value : "",
                    filterTierPage,
                    filterPortalPage,
                    filterBlacklistPage
                  ) as any
                );
              }}
            />
          ) : (
            <></>
          )}

          <Box
            display={"flex"}
            flexDirection={isMobile ? "column" : "row"}
            justifyContent={"left"}
            style={{ gap: 5 }}
          >
            <FilterKit
              orderByOptions={[
                {
                  key: memberTierFilters,
                  label: "Tier",
                  options: memberTierFilters.map((tier: any) => {
                    return {
                      label: tier,
                      value: tier !== "All" ? tier : "",
                    };
                  }),
                },
              ]}
              onChangeOrder={(e: any) => {
                dispatch(
                  getAllMembers(
                    1,
                    "",
                    limitPage,
                    filterTenantPage,
                    e[memberTierFilters] ? e[memberTierFilters].value : "",
                    filterPortalPage,
                    filterBlacklistPage
                  ) as any
                );
              }}
            />

            <FilterKit
              orderByOptions={[
                {
                  key: "ancolIdFilter",
                  label: "Ancol ID",
                  options: [
                    { label: "All", value: "" },
                    { label: "Verified", value: "not_null" },
                    { label: "Unverified", value: "null" },
                  ],
                },
              ]}
              onChangeOrder={(e: any) => {
                const selected = e["ancolIdFilter"]
                  ? e["ancolIdFilter"].value
                  : "";
                dispatch(
                  getAllMembers(
                    1,
                    "",
                    limitPage,
                    filterTenantPage,
                    filterTierPage,
                    selected,
                    filterBlacklistPage
                  ) as any
                );
              }}
            />

            <FilterKit
              orderByOptions={[
                {
                  key: "raffleBlacklistFilter",
                  label: "Raffle Blacklist",
                  options: [
                    { label: "All", value: "" },
                    { label: "Blacklisted", value: "1" },
                    { label: "Not Blacklisted", value: "0" },
                  ],
                },
              ]}
              onChangeOrder={(e: any) => {
                const selected = e["raffleBlacklistFilter"]
                  ? e["raffleBlacklistFilter"].value
                  : "";
                dispatch(
                  getAllMembers(
                    1,
                    "",
                    limitPage,
                    filterTenantPage,
                    filterTierPage,
                    filterPortalPage,
                    selected
                  ) as any
                );
              }}
            />
          </Box>
        </Box>
      </HeaderWrapper>

      <Card maxHeight={"100%"} overflow={"auto"} elevation={2}>
        <Table>
          <TableHead
            position={"sticky"}
            top={0}
            backgroundColor={theme.colors.grey02}
          >
            <TableHeadRow>
              <TableHeadCell>Ancol ID</TableHeadCell>
              <TableHeadCell>Name</TableHeadCell>
              <TableHeadCell>Email</TableHeadCell>
              <TableHeadCell>Phone Number</TableHeadCell>
              <TableHeadCell>Tier</TableHeadCell>
              {/* <TableHeadCell>Email Verified</TableHeadCell> */}
              <TableHeadCell>Phone Verified</TableHeadCell>
              {userRole === "superadmin" ? (
                <TableHeadCell minWidth={150}>Tenant</TableHeadCell>
              ) : (
                <></>
              )}
              <TableHeadCell>Raffle Blacklist</TableHeadCell>
              <TableHeadCell>Portal</TableHeadCell>
              <TableHeadCell>Action</TableHeadCell>
            </TableHeadRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodyRowsSkeleton
                rowsNumber={10}
                columnsNumber={userRole === "superadmin" ? 9 : 8}
              />
            ) : !members.length ? (
              <TableBodyRow>
                <TableBodyCell
                  colSpan={userRole === "superadmin" ? 9 : 8}
                  textAlign={"center"}
                >
                  No data available in table
                </TableBodyCell>
              </TableBodyRow>
            ) : (
              members.map((member: any) => {
                const name = member.name;
                const color = stringToColor(name);

                return (
                  <TableBodyRow key={member.id}>
                    <TableBodyCell>{member.ancol_id}</TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Avatar
                        name={name}
                        backgroundColor={color}
                        style={{ marginRight: 15 }}
                      />
                      {name}
                    </TableBodyCell>
                    <TableBodyCell>{member.email}</TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      +62 {member.phone_number.slice(2)}
                    </TableBodyCell>
                    <TableBodyCell whiteSpace={"nowrap"}>
                      <Badge
                        size="md"
                        style={{
                          backgroundImage: `${member.tier_color}`,
                          color: `${
                            member.tier_name !== "silver"
                              ? "#FAFBFD"
                              : "#949DA7"
                          }`,
                        }}
                      >
                        {toCapitilizeCase(member.tier_name)}
                      </Badge>
                    </TableBodyCell>
                    {/* <TableBodyCell>
                      <Badge
                        size={"md"}
                        variant={
                          member.email_is_verified !== 1
                            ? "critical"
                            : "success"
                        }
                      >
                        {member.email_is_verified !== 1 ? "No" : "Yes"}
                      </Badge>
                    </TableBodyCell>*/}
                    <TableBodyCell>
                      <Badge
                        size={"md"}
                        variant={
                          member.phone_is_verified !== 1
                            ? "critical"
                            : "success"
                        }
                      >
                        {member.phone_is_verified !== 1 ? "No" : "Yes"}
                      </Badge>
                    </TableBodyCell> 
                    {userRole === "superadmin" ? (
                      <TableBodyCell>
                        <Badge size={"md"} variant={"info"}>
                          {member.tenant_name}
                        </Badge>
                      </TableBodyCell>
                    ) : (
                      <></>
                    )}  
                    <TableBodyCell>
                      <InputCheckbox
                        checked={!!blacklistStates[member.id]}
                        onCheckedChange={() => handleBlacklistToggle(member.id)}
                      />
                    </TableBodyCell>
                    <TableBodyCell>
                      <Badge size={"md"} variant={"warning"}>
                        {member.portal_name}
                      </Badge>
                    </TableBodyCell>
                    <TableBodyCell>
                      <IconButton
                        onClick={() => handleOpenFormModal(member.id)}
                      >
                        <IconInfo fill={theme.colors.green06} />
                      </IconButton>
                    </TableBodyCell>
                  </TableBodyRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        paddingY={10}
      >
        {!isMobile ? (
          <PaginationDetail
            page={currentPage}
            limit={limitPage}
            length={lengthPage}
          />
        ) : (
          ""
        )}

        <Pagination
          current={currentPage}
          total={totalPage}
          onSelect={(select: number) =>
            dispatch(
              getAllMembers(
                select,
                searchValue,
                limitPage,
                filterTenantPage,
                filterTierPage,
                filterPortalPage,
                filterBlacklistPage
              ) as any
            )
          }
        />
        <PaginationFilter
          selectedItem={limit}
          onChange={(selectedItem) => {
            if (selectedItem) {
              setLimit(selectedItem);
              dispatch(
                getAllMembers(
                  1,
                  searchValue,
                  selectedItem,
                  filterTenantPage,
                  filterTierPage,
                  filterPortalPage,
                  filterBlacklistPage
                ) as any
              );
            }
          }}
          items={[10, 20, 30, 40, 50]}
        />
      </Box>

      <MemberDetailModal
        memberId={selectedMemberId}
        isOpen={isModalFormOpen}
        onClose={() => handleCloseFormModal()}
      />
    </Box>
  );
};

export default MembersContent;
